import type { CommerceFeature } from "@teqtank/commerce-contracts";
import { MockCommerceTransport } from "@teqtank/commerce-transport";
import { bootstrapCommerceContextOperation } from "../operations/bootstrap-commerce-context-operation.js";
import { bootstrapCommerceSessionOperation } from "../operations/bootstrap-commerce-session-operation.js";
import { switchCommerceMarketOperation } from "../operations/switch-commerce-market-operation.js";
import { registerDefaultProductOperations } from "../products/register-default-product-operations.js";
import { registerDefaultCartOperations } from "../cart/register-default-cart-operations.js";
import { registerDefaultCheckoutOperations } from "../checkout/register-default-checkout-operations.js";
import { registerDefaultOrderOperations } from "../orders/register-default-order-operations.js";
import { registerDefaultEnrollmentOperations } from "../enrollment/register-default-enrollment-operations.js";
import { registerDefaultStorefrontOperations } from "../storefront/register-default-storefront-operations.js";
import { registerDefaultShareOperations } from "../share/register-default-share-operations.js";
import { registerDefaultCustomerOperations } from "../customer/register-default-customer-operations.js";

export function createDefaultCommerceTransport(): MockCommerceTransport {
    const transport = new MockCommerceTransport();

    const features: readonly CommerceFeature[] = Object.freeze([
        "partner-storefront",
        "coupons",
        "customer-create",
        "guest-checkout",
        "email-verification-code",
        "inventory-visibility",
        "order-submit",
        "quick-buy",
        "rewards",
        "recurring",
    ]);

    transport.register(bootstrapCommerceSessionOperation, ({ commerceKey }) => ({
        sessionToken: `local-session:${commerceKey}`,
        commerceKey,
        dataSource: "playground" as const,
    }));

    const contextResponse = () => ({
        tenant: {
            id: "local-tenant",
            name: "Local Tenant",
        },

        storefront: {
            id: "local-storefront",
            alias: "default",
            name: "Local Storefront",
        },

        market: {
            id: "new-zealand",
            name: "New Zealand",
            countryCode: "NZ",
        },

        currency: {
            code: "NZD",
            symbol: "$",
            decimalPlaces: 2,
        },

        locale: {
            code: "en-NZ",
            languageCode: "en",
            timeZone: "Pacific/Auckland",
        },

        countries: [
            {
                code: "NZ",
                name: "New Zealand",
                currencies: [{ code: "NZD", symbol: "$", decimalPlaces: 2 }],
            },
        ],

        shippingCountries: [{ code: "NZ", name: "New Zealand" }],

        features,
    });

    transport.register(bootstrapCommerceContextOperation, contextResponse);
    transport.register(switchCommerceMarketOperation, () => ({
        context: contextResponse(),
        requiresConfirmation: false,
        cartCleared: false,
    }));

    registerDefaultProductOperations(transport);
    const cart = registerDefaultCartOperations(transport);
    const getCart = cart.current;
    const getEnrollment = registerDefaultEnrollmentOperations(transport, cart);
    const getCheckout = registerDefaultCheckoutOperations(transport, getCart, () =>
        getEnrollment()?.currentStage.kind === "checkout" ? "enrollment" : "shop",
    );
    registerDefaultOrderOperations(transport, getCart, getCheckout);
    registerDefaultCustomerOperations(transport);
    registerDefaultStorefrontOperations(transport);
    registerDefaultShareOperations(transport, cart);

    return transport;
}
